import DOMPurify from "dompurify";
import { useEffect, useState } from "react";
import { ChevronDown, Quote, Instagram, Facebook, Twitter, Linkedin, Youtube, Phone, Mail, MapPin, Clock, ShoppingBag, Zap, Search, Check, Play } from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR, fileUrl } from "../lib/api";
import { discountPct } from "../lib/cart";
import { resolveCta, ytId, ytThumb } from "../lib/cta";
import { themeVars, SafeImg, Stars } from "../components/StoreKit";

const lines = (s = "") => String(s).split("\n").map((x) => x.trim()).filter(Boolean);
const pairs = (s = "") => lines(s).map((l) => l.split("|").map((x) => (x || "").trim()));
const PADY = { sm: "py-6", md: "py-12", lg: "py-20" };
const AL = { left: "text-left", center: "text-center", right: "text-right" };
const JUST = { left: "justify-start", center: "justify-center", right: "justify-end" };
const IMGH = { sm: "h-48", md: "h-72", lg: "h-96" };

function Countdown({ target }) {
  const [left, setLeft] = useState("");
  useEffect(() => {
    const t = new Date(target).getTime();
    if (!target || isNaN(t)) { setLeft(""); return; }
    const tick = () => {
      const d = t - Date.now();
      if (d <= 0) return setLeft("0d 0h 0m 0s");
      setLeft(`${Math.floor(d / 86400000)}d ${Math.floor(d / 3600000) % 24}h ${Math.floor(d / 60000) % 60}m ${Math.floor(d / 1000) % 60}s`);
    };
    tick();
    const i = setInterval(tick, 1000);
    return () => clearInterval(i);
  }, [target]);
  if (!left) return null;
  return <span className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter" style={{ color: "var(--sp)" }}>{left}</span>;
}

function FaqList({ items, radius }) {
  const [open, setOpen] = useState(0);
  return (
    <div className="space-y-3 max-w-3xl mx-auto">
      {items.map(([q, a], i) => (
        <div key={i} className="bg-white border border-black/5 overflow-hidden" style={{ borderRadius: radius }}>
          <button type="button" onClick={(e) => { e.stopPropagation(); setOpen(open === i ? -1 : i); }} className="w-full flex items-center justify-between px-6 py-4 text-left">
            <span className="font-bold text-sm" style={{ color: "var(--sp)" }}>{q}</span>
            <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} style={{ color: "var(--sa)" }} />
          </button>
          {open === i && <p className="px-6 pb-4 text-sm" style={{ color: "var(--st)", opacity: 0.7 }}>{a}</p>}
        </div>
      ))}
    </div>
  );
}

function Slider({ images, radius }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (images.length < 2) return;
    const t = setInterval(() => setI((s) => (s + 1) % images.length), 3500);
    return () => clearInterval(t);
  }, [images.length]);
  if (!images.length) return null;
  return (
    <div className="relative overflow-hidden max-w-5xl mx-auto" style={{ borderRadius: radius }}>
      <SafeImg src={fileUrl(images[i])} alt="" className="w-full h-80 object-cover" />
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
        {images.map((_, d) => <span key={d} className="h-1.5 rounded-full bg-white transition-all" style={{ width: d === i ? 20 : 6, opacity: d === i ? 1 : 0.5 }} />)}
      </div>
    </div>
  );
}

/* Theme-native CTA button for service/pricing cards — resolves WhatsApp / payment / URL / internal / service-details actions. */
function CtaBtn({ cfg, live, slug, testid, full = false }) {
  const c = resolveCta(cfg, { slug });
  if (!cfg.buttonText) return null;
  const cls = `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold text-sm transition-all ${live ? "active:scale-95 hover:-translate-y-0.5 hover:shadow-lg" : ""} ${full ? "w-full" : ""}`;
  const style = { backgroundImage: "linear-gradient(135deg, var(--sp), var(--sa))", color: "#fff" };
  if (!c) return <span data-testid={testid} className={cls} style={style}>{cfg.buttonText}</span>;
  return (
    <a data-testid={testid} href={c.href} target={c.external ? "_blank" : undefined} rel={c.external ? "noreferrer" : undefined}
      onClick={live ? undefined : (e) => e.preventDefault()} className={cls} style={style}>{cfg.buttonText}</a>
  );
}

/* Click-to-play YouTube card — thumbnail first, real YouTube embed on click (no downloads, no hacks). */
function YtCard({ v, live, radius }) {
  const [play, setPlay] = useState(false);
  const id = ytId(v.youtubeUrl);
  const thumb = v.thumbnail || ytThumb(v.youtubeUrl);
  return (
    <div className="bg-white border border-black/5 overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-all duration-300" style={{ borderRadius: radius }} data-testid={`youtube-card-${v.id}`}>
      {play && live && id ? (
        <iframe src={`https://www.youtube.com/embed/${id}?autoplay=1&rel=0`} title={v.title || "YouTube video"}
          className="w-full aspect-video border-0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
      ) : (
        <button type="button" onClick={live && id ? () => setPlay(true) : undefined} className="relative block w-full aspect-video bg-zinc-900 group" data-testid={`youtube-play-${v.id}`}>
          {thumb
            ? <img src={fileUrl(thumb)} alt={v.title || ""} className="w-full h-full object-cover opacity-90 group-hover:opacity-70 transition-opacity" />
            : <div className="w-full h-full flex items-center justify-center text-white/40 text-xs px-6 text-center">Paste a YouTube URL in the section settings</div>}
          <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="h-14 w-14 rounded-full flex items-center justify-center text-white shadow-2xl transition-transform group-hover:scale-110" style={{ backgroundColor: "var(--sa)" }}>
              <Play className="h-6 w-6 ml-0.5" fill="currentColor" />
            </span>
          </span>
        </button>
      )}
      {(v.title || v.description) && (
        <div className="p-4">
          {v.title && <p className="font-display font-bold text-sm" style={{ color: "var(--sp)" }}>{v.title}</p>}
          {v.description && <p className="text-xs mt-1 opacity-65 leading-relaxed">{v.description}</p>}
        </div>
      )}
    </div>
  );
}

function Section({ sec, children }) {
  const st = sec.style || {};
  return (
    <section className={`${PADY[st.padY] || "py-12"} px-4 sm:px-6 ${sec.hideMobile ? "max-md:hidden" : ""}`}
      style={{ backgroundColor: st.bg || undefined, color: st.color || undefined }}
      data-section-type={sec.type}>
      <div className="max-w-7xl mx-auto">{children}</div>
    </section>
  );
}

/* Shared renderer — used by the visual editor canvas AND the live storefront for published content. */
export default function EditorCanvas({ doc, theme = {}, store = {}, info = {}, branding = {}, products = [], live = false, slug = "", handlers = {} }) {
  const design = doc?.design || {};
  const colors = { ...(theme.colors || {}), ...Object.fromEntries(Object.entries(design.colors || {}).filter(([, v]) => v)) };
  const th = { ...theme, colors, font: design.font || theme.font, body_font: design.body_font || theme.body_font, radius: design.radius || theme.radius };
  const vars = themeVars(colors, th);
  const radius = th.radius || "10px";
  const sections = (doc?.sections || []).filter((s) => !s.hidden);
  const socials = [
    ["instagram", Instagram, branding.social_instagram || info?.socials?.instagram],
    ["facebook", Facebook, branding.social_facebook || info?.socials?.facebook],
    ["twitter", Twitter, branding.social_twitter],
    ["linkedin", Linkedin, branding.social_linkedin],
    ["youtube", Youtube, branding.social_youtube],
  ].filter(([, , v]) => v || !live);

  const renderSection = (sec) => {
    const p = sec.props || {};
    const align = p.align || sec.style?.align || "left";
    if (live && (sec.type === "header" || sec.type === "footer")) return null;

    switch (sec.type) {
      case "header":
        return (
          <Section key={sec.id} sec={sec}>
            <div className="flex items-center gap-6 flex-wrap">
              {p.logo ? <img src={fileUrl(p.logo)} alt="logo" className="h-10 object-contain" /> : null}
              <span className="font-display font-extrabold text-xl" style={{ color: "var(--sp)" }}>{p.brandName || store.name || "Brand"}</span>
              <div className="flex gap-4 ml-4 flex-wrap">{String(p.menu || "").split(",").map((m) => m.trim()).filter(Boolean).map((m) => <span key={m} className="text-sm font-semibold opacity-70">{m}</span>)}</div>
              {p.ctaText && <span className="ml-auto px-5 py-2.5 rounded-full text-xs font-extrabold text-white" style={{ backgroundColor: "var(--sp)" }}>{p.ctaText}</span>}
            </div>
          </Section>
        );
      case "hero":
        return (
          <Section key={sec.id} sec={sec}>
            <div className={`grid lg:grid-cols-2 gap-10 items-center ${align === "center" ? "lg:grid-cols-1 text-center max-w-3xl mx-auto" : ""}`}>
              <div>
                {p.badge && <span className="inline-block px-4 py-1.5 rounded-full text-[11px] font-extrabold uppercase tracking-widest mb-4" style={{ backgroundColor: "var(--ss)", color: "var(--sp)" }}>{p.badge}</span>}
                <h1 className="fh text-4xl sm:text-6xl tracking-tighter leading-[1.02]" style={{ color: "var(--sp)" }}>{p.heading}</h1>
                {p.sub && <p className="mt-4 text-base sm:text-lg leading-relaxed opacity-70">{p.sub}</p>}
                <div className={`flex flex-wrap gap-3 mt-7 ${align === "center" ? "justify-center" : ""}`}>
                  {p.cta1 && (live
                    ? <button data-testid="canvas-hero-cta1" onClick={() => handlers.onShopNow?.()} className="px-7 py-3.5 rounded-full font-bold text-sm text-white shadow-lg active:scale-95 transition-transform" style={{ backgroundColor: "var(--sp)" }}>{p.cta1}</button>
                    : <span className="px-7 py-3.5 rounded-full font-bold text-sm text-white shadow-lg" style={{ backgroundColor: "var(--sp)" }}>{p.cta1}</span>)}
                  {p.cta2 && (live
                    ? <button data-testid="canvas-hero-cta2" onClick={() => (handlers.onSecondary || handlers.onShopNow)?.()} className="px-7 py-3.5 rounded-full font-bold text-sm border-2 active:scale-95 transition-transform" style={{ borderColor: "var(--sp)", color: "var(--sp)" }}>{p.cta2}</button>
                    : <span className="px-7 py-3.5 rounded-full font-bold text-sm border-2" style={{ borderColor: "var(--sp)", color: "var(--sp)" }}>{p.cta2}</span>)}
                </div>
              </div>
              {p.image && <SafeImg src={fileUrl(p.image)} alt="" className="w-full aspect-[4/3] object-cover shadow-2xl" style={{ borderRadius: `calc(${radius} * 2)` }} />}
            </div>
          </Section>
        );
      case "heading": {
        const Tag = ["h1", "h2", "h3"].includes(p.level) ? p.level : "h2";
        const sz = p.level === "h1" ? "text-4xl sm:text-5xl" : p.level === "h3" ? "text-2xl" : "text-3xl sm:text-4xl";
        return <Section key={sec.id} sec={sec}><Tag className={`fh ${sz} tracking-tighter ${AL[align]}`} style={{ color: "var(--sp)" }}>{p.text}</Tag></Section>;
      }
      case "text":
        return <Section key={sec.id} sec={sec}><p className={`text-base sm:text-lg leading-relaxed opacity-75 max-w-3xl ${AL[align]} ${align === "center" ? "mx-auto" : ""}`}>{p.text}</p></Section>;
      case "image":
        return <Section key={sec.id} sec={sec}><SafeImg src={fileUrl(p.src)} alt={p.alt || ""} className={`w-full ${IMGH[p.height] || "h-72"} object-cover`} style={{ borderRadius: radius, objectPosition: p.position || "center" }} /></Section>;
      case "video":
        return (
          <Section key={sec.id} sec={sec}>
            {p.url ? (p.url.includes("embed") || p.url.includes("youtube")
              ? <iframe src={p.url} title="video" className="w-full aspect-video" style={{ borderRadius: radius }} allowFullScreen />
              : <video src={fileUrl(p.url)} controls className="w-full" style={{ borderRadius: radius }} />)
              : <div className="w-full aspect-video flex items-center justify-center text-sm opacity-50 border border-dashed rounded-xl">Add a video embed URL</div>}
          </Section>
        );
      case "button":
        return (
          <Section key={sec.id} sec={sec}>
            <div className={`flex ${JUST[align]}`}>
              <a href={p.link || "#"} data-testid="canvas-button" onClick={live ? (p.link ? undefined : (e) => { e.preventDefault(); handlers.onShopNow?.(); }) : (e) => e.preventDefault()} className="px-8 py-3.5 rounded-full font-bold text-sm text-white shadow-lg inline-block" style={{ backgroundImage: "linear-gradient(135deg, var(--sp), var(--sa))" }}>{p.text || "Button"}</a>
            </div>
          </Section>
        );
      case "logo":
        return <Section key={sec.id} sec={sec}><div className={`flex ${JUST[align]}`}>{p.src ? <img src={fileUrl(p.src)} alt="logo" className="h-14 object-contain" /> : <span className="text-sm opacity-40">Upload a logo</span>}</div></Section>;
      case "gallery": {
        const imgs = lines(p.images).map(fileUrl);
        const cols = { 2: "grid-cols-2", 3: "grid-cols-2 sm:grid-cols-3", 4: "grid-cols-2 sm:grid-cols-4" }[p.cols] || "grid-cols-3";
        return <Section key={sec.id} sec={sec}><div className={`grid ${cols} gap-4`}>{imgs.map((src, i) => <SafeImg key={i} src={src} alt="" className="w-full aspect-square object-cover" style={{ borderRadius: radius }} />)}</div></Section>;
      }
      case "slider":
        return <Section key={sec.id} sec={sec}><Slider images={lines(p.images)} radius={radius} /></Section>;
      case "products": {
        const list = products.slice(0, Number(p.count) || 8);
        const cols = { 2: "grid-cols-2", 3: "grid-cols-2 lg:grid-cols-3", 4: "grid-cols-2 lg:grid-cols-4" }[p.cols] || "grid-cols-2 lg:grid-cols-4";
        return (
          <Section key={sec.id} sec={sec}>
            <h2 className="fh text-3xl sm:text-4xl tracking-tighter mb-8" style={{ color: "var(--sp)" }}>{p.title}</h2>
            <div className={`grid ${cols} gap-5`}>
              {list.map((pr) => (
                <div key={pr.id} className="bg-white border border-black/5 overflow-hidden group hover:-translate-y-1 hover:shadow-xl transition-all duration-300" style={{ borderRadius: radius }} data-testid={`canvas-product-${pr.id}`}>
                  <div className="relative aspect-square overflow-hidden cursor-pointer" onClick={live ? () => handlers.onOpen?.(pr) : undefined}>
                    <SafeImg src={pr.images?.[0]} alt={pr.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    {discountPct(pr) > 0 && <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[11px] font-extrabold text-white" style={{ backgroundColor: "var(--sa)" }}>-{discountPct(pr)}%</span>}
                  </div>
                  <div className="p-4">
                    <p className="font-bold text-sm truncate">{pr.name}</p>
                    <p className="font-display font-extrabold mt-1" style={{ color: "var(--sp)" }}>{fmtINR(pr.price)}</p>
                    {live && (
                      <div className="flex gap-2 mt-3">
                        <button onClick={() => handlers.onAdd?.(pr)} className="flex-1 py-2 text-xs font-bold border-2 rounded-full" style={{ borderColor: "var(--sp)", color: "var(--sp)" }}>Add</button>
                        <button onClick={() => handlers.onBuy?.(pr)} className="flex-1 py-2 text-xs font-bold text-white rounded-full" style={{ backgroundColor: "var(--sp)" }}>Buy Now</button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Section>
        );
      }
      case "categories": {
        const cats = [...new Set(products.map((x) => x.category).filter(Boolean))];
        return (
          <Section key={sec.id} sec={sec}>
            <h2 className="fh text-3xl tracking-tighter mb-6" style={{ color: "var(--sp)" }}>{p.title}</h2>
            <div className="flex flex-wrap gap-3">{cats.map((c) => live ? (
              <button key={c} data-testid={`canvas-cat-${String(c).toLowerCase()}`} onClick={() => handlers.onCategory?.(c)}
                className="px-5 py-2.5 rounded-full text-sm font-bold transition-transform hover:scale-105 active:scale-95"
                style={{ backgroundColor: "var(--ss)", color: "var(--sp)" }}>{c}</button>
            ) : (
              <span key={c} className="px-5 py-2.5 rounded-full text-sm font-bold" style={{ backgroundColor: "var(--ss)", color: "var(--sp)" }}>{c}</span>
            ))}</div>
          </Section>
        );
      }
      case "search":
        return (
          <Section key={sec.id} sec={sec}>
            <div className="relative max-w-xl mx-auto">
              <Search className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 opacity-40" />
              <input placeholder={p.placeholder || "Search…"} readOnly={!live} onChange={live ? (e) => handlers.onSearch?.(e.target.value) : undefined}
                className="w-full h-12 pl-11 pr-4 rounded-full text-sm bg-white border border-black/10 outline-none" />
            </div>
          </Section>
        );
      case "cart":
        return (
          <Section key={sec.id} sec={sec}>
            <div className={`flex ${JUST[align]}`}>
              <button onClick={live ? () => handlers.onCart?.() : undefined} className="px-8 py-3.5 rounded-full font-bold text-sm text-white shadow-lg inline-flex items-center gap-2" style={{ backgroundColor: "var(--sp)" }}>
                <ShoppingBag className="h-4 w-4" /> {p.text || "View Cart"}
              </button>
            </div>
          </Section>
        );
      case "pricing": {
        if (!Array.isArray(p.packages) || !p.packages.length) {
          return (
            <Section key={sec.id} sec={sec}>
              <h2 className="fh text-3xl sm:text-4xl tracking-tighter text-center mb-8" style={{ color: "var(--sp)" }}>{p.title}</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
                {pairs(p.items).map(([n, price, feats], i) => (
                  <div key={i} className="bg-white border border-black/5 p-7 hover:-translate-y-1 hover:shadow-xl transition-all" style={{ borderRadius: radius }}>
                    <p className="font-display font-bold text-lg" style={{ color: "var(--sp)" }}>{n}</p>
                    <p className="font-display font-extrabold text-3xl mt-2" style={{ color: "var(--sa)" }}>{price}</p>
                    <div className="mt-4 space-y-1.5">{String(feats || "").split(",").map((f) => f.trim()).filter(Boolean).map((f) => <p key={f} className="text-sm opacity-70">· {f}</p>)}</div>
                  </div>
                ))}
              </div>
            </Section>
          );
        }
        const layout = p.layout || "grid3";
        const priceOf = (pk) => {
          const cur = pk.currency ?? "₹";
          const show = (v) => (/^[0-9]/.test(String(v || "").trim()) ? `${cur}${v}` : v);
          return { main: pk.offerPrice ? show(pk.offerPrice) : show(pk.price), strike: pk.offerPrice ? show(pk.price) : "" };
        };
        if (layout === "minimal") {
          return (
            <Section key={sec.id} sec={sec}>
              <h2 className="fh text-3xl sm:text-4xl tracking-tighter mb-2" style={{ color: "var(--sp)" }}>{p.title}</h2>
              {p.sub && <p className="text-sm opacity-60 mb-8">{p.sub}</p>}
              <div className="max-w-3xl space-y-4" data-testid="canvas-pricing">
                {p.packages.map((pk) => {
                  const pr = priceOf(pk);
                  return (
                    <div key={pk.id} className={`bg-white p-6 flex flex-col sm:flex-row sm:items-center gap-4 transition-all ${pk.highlighted ? "border-2 shadow-xl" : "border border-black/5 hover:shadow-lg"}`}
                      style={{ borderRadius: radius, borderColor: pk.highlighted ? "var(--sa)" : undefined }} data-testid={`pricing-card-${pk.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-display font-bold" style={{ color: "var(--sp)" }}>{pk.name}</p>
                          {pk.highlighted && <span className="px-2.5 py-0.5 rounded-full text-[9px] font-extrabold uppercase tracking-widest text-white" style={{ backgroundColor: "var(--sa)" }}>Recommended</span>}
                        </div>
                        {pk.description && <p className="text-xs opacity-60 mt-1">{pk.description}</p>}
                        {Array.isArray(pk.features) && pk.features.filter(Boolean).length > 0 && (
                          <p className="text-xs opacity-60 mt-2">{pk.features.filter(Boolean).join(" · ")}</p>
                        )}
                      </div>
                      <div className="sm:text-right shrink-0">
                        {pr.strike && <span className="text-xs line-through opacity-40 mr-2">{pr.strike}</span>}
                        <span className="font-display font-extrabold text-2xl" style={{ color: pk.highlighted ? "var(--sa)" : "var(--sp)" }}>{pr.main}</span>
                        {pk.billingPeriod && <span className="text-xs opacity-50"> /{pk.billingPeriod}</span>}
                        <div className="mt-2"><CtaBtn cfg={pk} live={live} slug={slug} testid={`pricing-cta-${pk.id}`} /></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Section>
          );
        }
        const grid = layout === "grid4"
          ? "grid sm:grid-cols-2 lg:grid-cols-4 gap-5 items-start"
          : "grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto items-start";
        return (
          <Section key={sec.id} sec={sec}>
            <h2 className="fh text-3xl sm:text-4xl tracking-tighter text-center mb-2" style={{ color: "var(--sp)" }}>{p.title}</h2>
            {p.sub && <p className="text-sm opacity-60 text-center mb-10 max-w-2xl mx-auto">{p.sub}</p>}
            {!p.sub && <div className="mb-8" />}
            <div className={grid} data-testid="canvas-pricing">
              {p.packages.map((pk) => {
                const pr = priceOf(pk);
                const feats = (Array.isArray(pk.features) ? pk.features : []).filter(Boolean);
                return (
                  <div key={pk.id}
                    className={`relative bg-white p-7 flex flex-col transition-all duration-300 ${pk.highlighted ? "border-2 shadow-2xl lg:-translate-y-3 z-10" : "border border-black/5 hover:-translate-y-1 hover:shadow-xl"}`}
                    style={{ borderRadius: radius, borderColor: pk.highlighted ? "var(--sa)" : undefined }} data-testid={`pricing-card-${pk.id}`}>
                    {pk.highlighted && (
                      <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full text-[10px] font-extrabold uppercase tracking-widest text-white whitespace-nowrap shadow-lg"
                        style={{ backgroundColor: "var(--sa)" }}>Recommended</span>
                    )}
                    {pk.icon && <img src={fileUrl(pk.icon)} alt="" className="h-10 w-10 object-contain mb-3" />}
                    <p className="font-display font-bold text-lg" style={{ color: "var(--sp)" }}>{pk.name}</p>
                    {pk.subtitle && <p className="text-xs opacity-55 mt-0.5">{pk.subtitle}</p>}
                    <div className="flex items-baseline gap-2 mt-4 flex-wrap">
                      <span className="font-display font-extrabold text-3xl tracking-tight" style={{ color: pk.highlighted ? "var(--sa)" : "var(--sp)" }}>{pr.main}</span>
                      {pr.strike && <span className="text-sm line-through opacity-40">{pr.strike}</span>}
                      {pk.billingPeriod && <span className="text-xs opacity-50">/{pk.billingPeriod}</span>}
                    </div>
                    {pk.description && <p className="text-xs opacity-60 mt-3 leading-relaxed">{pk.description}</p>}
                    {feats.length > 0 && (
                      <div className="mt-5 space-y-2">
                        {feats.map((f) => (
                          <p key={f} className="text-sm flex items-start gap-2 opacity-80">
                            <Check className="h-4 w-4 shrink-0 mt-0.5" style={{ color: "var(--sa)" }} /> {f}
                          </p>
                        ))}
                      </div>
                    )}
                    <div className="mt-auto pt-6"><CtaBtn cfg={pk} live={live} slug={slug} full testid={`pricing-cta-${pk.id}`} /></div>
                  </div>
                );
              })}
            </div>
          </Section>
        );
      }
      case "services": {
        if (!Array.isArray(p.cards) || !p.cards.length) {
          return (
            <Section key={sec.id} sec={sec}>
              <h2 className="fh text-3xl sm:text-4xl tracking-tighter mb-8" style={{ color: "var(--sp)" }}>{p.title}</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {pairs(p.items).map(([n, d], i) => (
                  <div key={i} className="bg-white border border-black/5 p-6 hover:-translate-y-1 hover:shadow-xl transition-all" style={{ borderRadius: radius }}>
                    <div className="h-10 w-10 rounded-xl flex items-center justify-center text-white mb-4" style={{ backgroundImage: "linear-gradient(135deg, var(--sp), var(--sa))" }}><Zap className="h-4 w-4" /></div>
                    <p className="font-display font-bold" style={{ color: "var(--sp)" }}>{n}</p>
                    <p className="text-sm mt-1 opacity-65">{d}</p>
                  </div>
                ))}
              </div>
            </Section>
          );
        }
        const grid = (p.layout || "grid3") === "grid2"
          ? "grid sm:grid-cols-2 gap-6 max-w-5xl mx-auto"
          : "grid sm:grid-cols-2 lg:grid-cols-3 gap-6";
        return (
          <Section key={sec.id} sec={sec}>
            <h2 className="fh text-3xl sm:text-4xl tracking-tighter mb-2" style={{ color: "var(--sp)" }}>{p.title}</h2>
            {p.sub && <p className="text-sm opacity-60 mb-8 max-w-2xl">{p.sub}</p>}
            {!p.sub && <div className="mb-6" />}
            <div className={grid} data-testid="canvas-services">
              {p.cards.map((c) => {
                const includes = (Array.isArray(c.includes) ? c.includes : []).filter(Boolean);
                return (
                  <div key={c.id} className="bg-white border border-black/5 overflow-hidden flex flex-col hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                    style={{ borderRadius: radius }} data-testid={`service-card-${c.id}`}>
                    {c.image && <SafeImg src={fileUrl(c.image)} alt={c.name || ""} className="w-full aspect-[16/10] object-cover" />}
                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <p className="font-display font-bold text-lg leading-snug" style={{ color: "var(--sp)" }}>{c.name}</p>
                        {c.duration && (
                          <span className="shrink-0 px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wide whitespace-nowrap"
                            style={{ backgroundColor: "var(--ss)", color: "var(--sp)" }}>{c.duration}</span>
                        )}
                      </div>
                      {c.description && <p className="text-sm mt-2 opacity-65 leading-relaxed">{c.description}</p>}
                      {includes.length > 0 && (
                        <div className="mt-4 space-y-1.5">
                          {includes.map((f) => (
                            <p key={f} className="text-sm flex items-start gap-2 opacity-80">
                              <Check className="h-4 w-4 shrink-0 mt-0.5" style={{ color: "var(--sa)" }} /> {f}
                            </p>
                          ))}
                        </div>
                      )}
                      <div className="mt-auto pt-5">
                        {(c.price || c.offerPrice) && (
                          <div className="flex items-baseline gap-2 mb-4 flex-wrap">
                            {c.offerPrice
                              ? <><span className="font-display font-extrabold text-2xl tracking-tight" style={{ color: "var(--sa)" }}>{c.offerPrice}</span><span className="text-sm line-through opacity-40">{c.price}</span></>
                              : <span className="font-display font-extrabold text-2xl tracking-tight" style={{ color: "var(--sp)" }}>{c.price}</span>}
                          </div>
                        )}
                        <CtaBtn cfg={c} live={live} slug={slug} full testid={`service-cta-${c.id}`} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>
        );
      }
      case "youtube": {
        let vids = [...(Array.isArray(p.videos) ? p.videos : [])]
          .filter((v) => v.active !== false)
          .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
        if (live) vids = vids.filter((v) => ytId(v.youtubeUrl));
        if (!vids.length) return live ? null : (
          <Section key={sec.id} sec={sec}>
            <h2 className="fh text-3xl sm:text-4xl tracking-tighter mb-6" style={{ color: "var(--sp)" }}>{p.title}</h2>
            <div className="w-full aspect-video max-w-2xl flex items-center justify-center text-sm opacity-50 border border-dashed rounded-xl">Add videos from the section settings</div>
          </Section>
        );
        const layout = p.layout || "grid3";
        return (
          <Section key={sec.id} sec={sec}>
            <h2 className="fh text-3xl sm:text-4xl tracking-tighter mb-2" style={{ color: "var(--sp)" }}>{p.title}</h2>
            {p.sub && <p className="text-sm opacity-60 mb-8 max-w-2xl">{p.sub}</p>}
            {!p.sub && <div className="mb-6" />}
            {layout === "featured" ? (
              <div className="grid lg:grid-cols-3 gap-5" data-testid="canvas-youtube">
                <div className="lg:col-span-2"><YtCard v={vids[0]} live={live} radius={radius} /></div>
                <div className="space-y-5">{vids.slice(1).map((v) => <YtCard key={v.id} v={v} live={live} radius={radius} />)}</div>
              </div>
            ) : layout === "list" ? (
              <div className="grid gap-5 max-w-3xl mx-auto" data-testid="canvas-youtube">
                {vids.map((v) => <YtCard key={v.id} v={v} live={live} radius={radius} />)}
              </div>
            ) : (
              <div className={layout === "grid2" ? "grid sm:grid-cols-2 gap-5 max-w-4xl mx-auto" : "grid sm:grid-cols-2 lg:grid-cols-3 gap-5"} data-testid="canvas-youtube">
                {vids.map((v) => <YtCard key={v.id} v={v} live={live} radius={radius} />)}
              </div>
            )}
          </Section>
        );
      }
      case "team":
        return (
          <Section key={sec.id} sec={sec}>
            <h2 className="fh text-3xl sm:text-4xl tracking-tighter text-center mb-8" style={{ color: "var(--sp)" }}>{p.title}</h2>
            <div className="flex flex-wrap justify-center gap-5">
              {pairs(p.items).map(([n, r], i) => (
                <div key={i} className="bg-white border border-black/5 px-8 py-6 text-center" style={{ borderRadius: radius }}>
                  <div className="h-14 w-14 mx-auto rounded-full flex items-center justify-center text-white font-display font-extrabold text-lg" style={{ backgroundImage: "linear-gradient(135deg, var(--sp), var(--sa))" }}>{String(n || "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}</div>
                  <p className="font-bold text-sm mt-3" style={{ color: "var(--sp)" }}>{n}</p>
                  <p className="text-xs opacity-55">{r}</p>
                </div>
              ))}
            </div>
          </Section>
        );
      case "stats":
        return (
          <Section key={sec.id} sec={sec}>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 text-center rounded-2xl py-10 px-6 text-white" style={{ backgroundImage: "linear-gradient(135deg, var(--sp), var(--sa))" }}>
              {pairs(p.items).map(([v, l], i) => (
                <div key={i}><p className="font-display font-extrabold text-4xl tracking-tighter">{v}</p><p className="text-xs font-bold uppercase tracking-widest mt-1.5 text-white/75">{l}</p></div>
              ))}
            </div>
          </Section>
        );
      case "testimonials":
        return (
          <Section key={sec.id} sec={sec}>
            <h2 className="fh text-3xl sm:text-4xl tracking-tighter text-center mb-8" style={{ color: "var(--sp)" }}>{p.title}</h2>
            <div className="grid sm:grid-cols-3 gap-5">
              {pairs(p.items).map(([n, q], i) => (
                <div key={i} className="bg-white border border-black/5 p-6" style={{ borderRadius: radius }}>
                  <Quote className="h-5 w-5 mb-3" style={{ color: "var(--sa)" }} />
                  <p className="text-sm leading-relaxed opacity-75">"{q}"</p>
                  <p className="text-xs font-extrabold mt-4" style={{ color: "var(--sp)" }}>{n}</p>
                </div>
              ))}
            </div>
          </Section>
        );
      case "reviews":
        return (
          <Section key={sec.id} sec={sec}>
            <h2 className="fh text-3xl tracking-tighter mb-6" style={{ color: "var(--sp)" }}>{p.title}</h2>
            <div className="grid sm:grid-cols-3 gap-5">
              {pairs(p.items).map(([n, r, q], i) => (
                <div key={i} className="bg-white border border-black/5 p-5" style={{ borderRadius: radius }}>
                  <Stars rating={Number(r) || 5} />
                  <p className="text-sm mt-2 opacity-75">"{q}"</p>
                  <p className="text-xs font-extrabold mt-3" style={{ color: "var(--sp)" }}>{n}</p>
                </div>
              ))}
            </div>
          </Section>
        );
      case "faq":
        return <Section key={sec.id} sec={sec}><h2 className="fh text-3xl sm:text-4xl tracking-tighter text-center mb-8" style={{ color: "var(--sp)" }}>{p.title}</h2><FaqList items={pairs(p.items)} radius={radius} /></Section>;
      case "contact":
        return (
          <Section key={sec.id} sec={sec}>
            <h2 className="fh text-3xl sm:text-4xl tracking-tighter mb-8" style={{ color: "var(--sp)" }}>{p.title}</h2>
            <div className="grid lg:grid-cols-2 gap-8">
              <div className="space-y-3">
                {[[Phone, branding.contact_phone || info.phone], [Mail, branding.contact_email || info.email], [MapPin, info.address], [Clock, info.hours]].filter(([, v]) => v).map(([Icon, v], i) => (
                  <div key={i} className="flex items-center gap-3 bg-white border border-black/5 p-4" style={{ borderRadius: radius }}>
                    <Icon className="h-4 w-4 shrink-0" style={{ color: "var(--sa)" }} /><span className="text-sm font-semibold">{v}</span>
                  </div>
                ))}
              </div>
              {p.showForm !== false && (
                <form data-testid="contact-form" onSubmit={async (e) => {
                  e.preventDefault();
                  if (!live) { toast.success("Demo form"); return; }
                  const fd = new FormData(e.target);
                  try {
                    await api.post(`/public/store/${slug}/enquiry`, {
                      name: fd.get("name"), email: fd.get("email"), phone: fd.get("phone"), message: fd.get("message"),
                    });
                    toast.success("Message sent! We will get back to you soon.");
                    e.target.reset();
                  } catch (err2) {
                    toast.error(err2?.response?.data?.detail || "Could not send. Please try again.");
                  }
                }} className="space-y-3">
                  <input required name="name" placeholder="Your name" className="w-full h-11 px-4 rounded-xl text-sm bg-white border border-black/10 outline-none" />
                  <input name="phone" placeholder="Phone / WhatsApp (optional)" className="w-full h-11 px-4 rounded-xl text-sm bg-white border border-black/10 outline-none" />
                  <input required name="email" type="email" placeholder="Your email" className="w-full h-11 px-4 rounded-xl text-sm bg-white border border-black/10 outline-none" />
                  <textarea required name="message" placeholder="Message" rows={3} className="w-full px-4 py-3 rounded-xl text-sm bg-white border border-black/10 outline-none" />
                  <button data-testid="contact-submit" className="px-7 py-3 rounded-full font-bold text-sm text-white" style={{ backgroundColor: "var(--sp)" }}>Send Message</button>
                </form>
              )}
            </div>
          </Section>
        );
      case "map":
        return (
          <Section key={sec.id} sec={sec}>
            <iframe title="map" src={`https://www.google.com/maps?q=${encodeURIComponent(p.address || "Mumbai")}&output=embed`}
              className="w-full h-80 border-0" style={{ borderRadius: radius }} loading="lazy" />
          </Section>
        );
      case "social":
        return (
          <Section key={sec.id} sec={sec}>
            <div className={`flex gap-3 ${JUST[align]}`}>
              {socials.map(([k, Icon, v]) => (
                <a key={k} href={v ? (String(v).startsWith("http") ? v : `https://${k}.com/${String(v).replace("@", "")}`) : "#"} target="_blank" rel="noreferrer"
                  onClick={live ? undefined : (e) => e.preventDefault()}
                  className="h-11 w-11 rounded-full flex items-center justify-center text-white transition-transform hover:scale-110" style={{ backgroundColor: "var(--sp)" }}>
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </Section>
        );
      case "newsletter":
        return (
          <Section key={sec.id} sec={sec}>
            <div className="rounded-2xl p-10 text-center text-white relative overflow-hidden" style={{ backgroundImage: "linear-gradient(135deg, var(--sp), var(--sa))", borderRadius: `calc(${radius} * 2)` }}>
              <h2 className="fh text-3xl tracking-tighter">{p.title}</h2>
              {p.sub && <p className="text-white/80 mt-2 text-sm">{p.sub}</p>}
              <form onSubmit={(e) => { e.preventDefault(); toast.success(live ? "Subscribed!" : "Demo form"); }} className="max-w-md mx-auto mt-6 flex gap-2">
                <input required type="email" placeholder="you@email.com" readOnly={!live} className="flex-1 h-12 px-4 rounded-full bg-white/95 text-zinc-900 text-sm border-0 outline-none" />
                <button className="h-12 px-6 rounded-full bg-zinc-900 text-white font-bold text-sm shrink-0">Subscribe</button>
              </form>
            </div>
          </Section>
        );
      case "countdown":
        return (
          <Section key={sec.id} sec={sec}>
            <div className="text-center">
              <p className="uppercase text-[11px] tracking-[0.3em] font-extrabold mb-3" style={{ color: "var(--sa)" }}>{p.title}</p>
              <Countdown target={p.target} />
              {!p.target && <p className="text-sm opacity-40">Set a target date</p>}
            </div>
          </Section>
        );
      case "cta":
        return (
          <Section key={sec.id} sec={sec}>
            <div className="rounded-3xl p-10 sm:p-14 text-center text-white relative overflow-hidden" style={{ backgroundImage: "linear-gradient(135deg, var(--sp), var(--sa))" }}>
              <h2 className="fh text-3xl sm:text-4xl tracking-tighter">{p.title}</h2>
              {p.button && <a href={p.link || "#"} data-testid="canvas-cta-button" onClick={live ? (p.link ? undefined : (e) => { e.preventDefault(); handlers.onShopNow?.(); }) : (e) => e.preventDefault()} className="inline-block mt-7 px-9 py-4 rounded-full bg-white font-bold text-sm" style={{ color: "var(--sp)" }}>{p.button}</a>}
            </div>
          </Section>
        );
      case "spacer":
        return <div key={sec.id} style={{ height: Number(p.height) || 48 }} className={sec.hideMobile ? "max-md:hidden" : ""} />;
      case "divider":
        return <Section key={sec.id} sec={{ ...sec, style: { ...sec.style, padY: "sm" } }}><hr style={{ borderColor: "var(--st)", opacity: 0.12 }} /></Section>;
      case "html":
        return <Section key={sec.id} sec={sec}><div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(p.html || "") }} /></Section>;
      case "footer":
        return (
          <Section key={sec.id} sec={sec}>
            <div className="rounded-2xl p-8 text-white" style={{ backgroundColor: "var(--sp)", borderRadius: `calc(${radius} * 1.5)` }}>
              <div className="grid sm:grid-cols-3 gap-8">
                <div>
                  <p className="font-display font-extrabold text-lg">{store.name || "Brand"}</p>
                  <p className="text-sm text-white/60 mt-2">{p.description || info.description || branding.tagline || ""}</p>
                </div>
                {pairs(p.columns).map(([title, linksTxt], i) => (
                  <div key={i}>
                    <p className="text-[11px] uppercase tracking-widest font-extrabold text-white/50 mb-3">{title}</p>
                    <div className="space-y-2">{String(linksTxt || "").split(",").map((l) => l.trim()).filter(Boolean).map((l) => <p key={l} className="text-sm text-white/75">{l}</p>)}</div>
                  </div>
                ))}
              </div>
              {p.showSocial !== false && <div className="flex gap-2 mt-6">{socials.slice(0, 4).map(([k, Icon]) => <span key={k} className="h-9 w-9 rounded-full bg-white/10 flex items-center justify-center"><Icon className="h-4 w-4" /></span>)}</div>}
              <p className="border-t border-white/10 mt-6 pt-4 text-center text-xs text-white/40">{p.copyright || `© ${new Date().getFullYear()} ${store.name || "Brand"}`}</p>
            </div>
          </Section>
        );
      default:
        return null;
    }
  };

  return (
    <div style={{ ...vars, fontFamily: "var(--sbfont)" }} className="bg-[var(--sbg)] text-[var(--st)]" data-testid="editor-canvas">
      {sections.map(renderSection)}
    </div>
  );
}
